import { fromStored } from '../../../annotation-format';
import type { ViewerShell } from '../../../shell';
import { annotationsReadOnly } from '../../../tools';
import type { Viewport } from '../../../viewport';
import { jumpTo } from '../actions';
import { conversationStudy, frameAt, viewportContextOf } from '../context';
import { aiLimitText } from '../errors';
import type { ViewportContext } from '../api';
import type { MeasureSuggestion } from './api';
import { axes, button, el, mm, resultCard, type ToolDeps } from './dom';
import { longAxisLine } from './geometry';
import { trackLesion } from './lesions';
import { MARK_COLORS, removeMark, setMark } from './marks';
import { pickPoint } from './picker';

/**
 * "Medir con IA": a click on a lesion → ris suggests its box and long / short axes → the suggestion
 * is drawn dashed. Aceptar turns it into a normal Length measurement through cornerstone-tools (the
 * same path a drawn line takes, so the workspace lists it and Guardar saves it); Descartar removes it.
 */

export const MEASURE_TEXT = {
  button: 'Medir con IA',
  title: 'Medición sugerida',
  noImage: 'Muestre una imagen del estudio en el visor activo para medir.',
  thinking: 'midiendo…',
  notFound: 'El Asistente Guru no encontró una lesión medible en ese punto.',
  failed: 'No fue posible obtener la medición sugerida.',
  note: 'Sugerencia: no es una anotación hasta que la acepte.',
  accept: 'Aceptar',
  discard: 'Descartar',
  accepted: (value: string) => `Medición creada como línea (${value}).`,
  discarded: 'Sugerencia descartada.',
  replaced: 'Sugerencia reemplazada por una nueva medición.',
  readOnly: 'Las anotaciones están en solo lectura: vuelva a la versión actual para medir.',
  notShown: 'La imagen de la sugerencia ya no está en el visor.',
  track: 'Seguir lesión',
  confidence: 'confianza',
} as const;

const MARK_ID = 'measure-suggestion';
const PERCENT = 100;
/** How long Aceptar waits for the suggestion's slice to render after jumping back to it. */
const JUMP_WAIT_MS = 3000;

interface Pending {
  viewport: ViewportContext;
  suggestion: MeasureSuggestion;
  close: (text: string) => void;
}

let pending: Pending | null = null;

function shows(vp: Viewport, location: ViewportContext): boolean {
  const stack = vp.currentStack;
  const index = vp.currentIndex();
  if (!stack || index === undefined || !vp.currentImage()) return false;
  const at = frameAt(stack.series, index);
  return at?.instance.sopInstanceUID === location.instance_uid && at.frame === location.frame;
}

/** A viewport showing the suggestion's image, jumping the active one back to it when none does. */
async function viewportShowing(shell: ViewerShell, location: ViewportContext): Promise<Viewport | null> {
  const found = shell.viewports().find((vp) => shows(vp, location));
  if (found) return found;
  if (!jumpTo(shell, { ...location, box: null })) return null;
  return new Promise((resolve) => {
    const finish = (vp: Viewport | null) => {
      off();
      clearTimeout(timer);
      resolve(vp);
    };
    const off = shell.on('image-rendered', ({ viewport }) => {
      if (shows(viewport, location)) finish(viewport);
    });
    const timer = setTimeout(() => finish(null), JUMP_WAIT_MS);
  });
}

/** Adds the long axis as a Length measurement on `vp` (its current image is the suggestion's). */
function addLength(vp: Viewport, suggestion: MeasureSuggestion): boolean {
  const image = vp.currentImage();
  if (!image) return false;
  const spacing = image.rowPixelSpacing && image.columnPixelSpacing ? { row: image.rowPixelSpacing, column: image.columnPixelSpacing } : null;
  const points = longAxisLine(suggestion.box, image.columns, image.rows, spacing);
  const measurement = fromStored({ type: 'LINE', points, results: [[suggestion.longMm ?? 0, 'mm', 'length']] });
  if (!measurement) return false;
  window.cornerstoneTools.addToolState(vp.element, measurement.toolName, measurement.data);
  window.cornerstone.updateImage(vp.element);
  return true;
}

/** Aceptar / Descartar disabled while Aceptar waits for the slice, so a double click adds one line. */
function setBusy(actions: HTMLElement, busy: boolean): void {
  actions.querySelectorAll('button').forEach((b) => {
    b.disabled = busy;
  });
}

async function accept(deps: ToolDeps, current: Pending, actions: HTMLElement): Promise<void> {
  if (annotationsReadOnly()) return void deps.shell.toast(MEASURE_TEXT.readOnly, 'warning');
  if (pending !== current) return;
  setBusy(actions, true);
  const vp = await viewportShowing(deps.shell, current.viewport);
  setBusy(actions, false);
  if (pending !== current) return;
  if (!vp || !addLength(vp, current.suggestion)) return void deps.shell.toast(MEASURE_TEXT.notShown, 'warning');
  current.close(MEASURE_TEXT.accepted(mm(current.suggestion.longMm)));
  const { viewport, suggestion } = current;
  const location = { series_uid: viewport.series_uid, instance_uid: viewport.instance_uid, frame: viewport.frame, index: viewport.index, box: suggestion.box };
  actions.appendChild(button(MEASURE_TEXT.track, 'assistant-track-lesion', () =>
    void trackLesion(deps, { location, longMm: suggestion.longMm, shortMm: suggestion.shortMm })));
}

function summary(s: MeasureSuggestion): string {
  const confidence = s.confidence === null ? '' : ` · ${MEASURE_TEXT.confidence} ${Math.round(s.confidence * PERCENT)} %`;
  return `${axes(s.longMm, s.shortMm)}${confidence}`;
}

function showSuggestion(deps: ToolDeps, viewport: ViewportContext, suggestion: MeasureSuggestion, study: string): void {
  const { card, body } = resultCard('measure', MEASURE_TEXT.title);
  body.appendChild(el('div', 'assistant-measure-value', summary(suggestion)));
  const status = el('div', 'assistant-tool-note', MEASURE_TEXT.note);
  const actions = el('div', 'assistant-finding-actions');
  body.append(status, actions);
  // The card leaves the log when the conversation reloads (another study): the dashed mark goes with it.
  const detached = new MutationObserver(() => {
    if (!card.isConnected) current.close(MEASURE_TEXT.discarded);
  });
  const current: Pending = {
    viewport,
    suggestion,
    close: (text) => {
      detached.disconnect();
      if (pending !== current) return;
      pending = null;
      removeMark(MARK_ID);
      status.textContent = text;
      actions.replaceChildren();
    },
  };
  actions.append(
    button(MEASURE_TEXT.accept, 'assistant-accept assistant-measure-accept', () => void accept(deps, current, actions)),
    button(MEASURE_TEXT.discard, 'assistant-reject assistant-measure-discard', () => current.close(MEASURE_TEXT.discarded)),
  );
  pending?.close(MEASURE_TEXT.replaced);
  pending = current;
  setMark({ id: MARK_ID, instanceUid: viewport.instance_uid, frame: viewport.frame, box: suggestion.box, label: axes(suggestion.longMm, suggestion.shortMm), color: MARK_COLORS.suggestion });
  deps.panel.appendResult(card, study);
  if (!card.isConnected) return current.close(MEASURE_TEXT.discarded);
  detached.observe(deps.panel.root, { childList: true, subtree: true });
}

export async function measureWithAi(deps: ToolDeps): Promise<void> {
  const { shell, api, panel } = deps;
  // A pending suggestion stays until a new one replaces it: cancelling the pick keeps it.
  const study = conversationStudy(shell);
  const picked = await pickPoint(shell);
  if (!picked) return;
  const viewport = viewportContextOf(picked.target.viewport, study);
  if (!viewport) return void shell.toast(MEASURE_TEXT.noImage, 'warning');
  const thinking = el('div', 'assistant-thinking', MEASURE_TEXT.thinking);
  thinking.setAttribute('role', 'status');
  panel.appendResult(thinking, study);
  try {
    const suggestion = await api.measure(study, viewport, picked.point);
    if (suggestion) showSuggestion(deps, viewport, suggestion, study);
    else shell.toast(MEASURE_TEXT.notFound, 'warning');
  } catch (error) {
    console.warn('[viewer] assisted measurement failed', error);
    shell.toast(aiLimitText(error) ?? MEASURE_TEXT.failed, 'error');
  } finally {
    panel.removeResult(thinking);
  }
}
